import { CheckOutIdentity, DocumentCheckOutAdapter } from "../types";

// Reading the document's identity once per session instead of once per ribbon press.
//
// The <jyloCheckOut> stamp cannot change while the document is open. The API writes it
// before the file is ever served, nothing in the add-in rewrites it, and a shared
// runtime belongs to exactly one document -- so "which document is this" is answered
// once and is then a fact for the rest of the session.
//
// Re-reading it per press was not just wasteful, it was the bug. Excel refuses the
// Rich API while a cell is in edit mode, and pressing a ribbon button does NOT leave
// edit mode -- so a Save straight out of typing could never read the stamp, and
// reported the document as not checked out. Reading at pane mount, when the user is by
// definition not mid-edit, takes that failure mode off the table entirely.
//
// This caches the IDENTITY, never the verification. Whether the draft is still open,
// and still held by this user, is a question about the server and is re-asked on every
// press -- see useCheckOutVerification. Caching the answer to "which document" does not
// make a stale check-out any harder to notice.

let pending: Promise<CheckOutIdentity | null> | null = null;

/**
 * The document's identity, read at most once.
 *
 * Concurrent callers share one read. A FAILED read is deliberately not cached: the
 * first attempt may well be the one that hit cell edit mode, and caching that would
 * turn a transient refusal into a permanent one for the rest of the session.
 */
export function readCheckOutIdentityOnce(
  adapter: DocumentCheckOutAdapter
): Promise<CheckOutIdentity | null> {
  if (!pending) {
    pending = adapter.readCheckOutIdentity().catch((error) => {
      pending = null;
      throw error;
    });
  }
  return pending;
}

/**
 * Warms the cache without caring about the result.
 *
 * Called when the pane mounts, which is the moment that makes the whole thing work:
 * the document has just opened, nothing is being edited, and the read is certain to
 * succeed. By the time any ribbon button is pressed the answer is already in hand.
 */
export function warmCheckOutIdentity(adapter: DocumentCheckOutAdapter): void {
  void readCheckOutIdentityOnce(adapter).catch(() => {
    // Nothing to do here -- a failure clears the cache above, and whoever actually
    // needs the identity will ask again and get the real error to show.
  });
}

/** Drops the cached identity. For the recovery path that rewrites the stamp. */
export function forgetCheckOutIdentity(): void {
  pending = null;
}
